interface CacheKeyMap {
  [key: string]: (data: never) => string | null;
}

/**
 * How graphcache identifies each type. Anything not named here falls back to `id`, which is the
 * right default for a record and the wrong one for a value object whose `id` is only unique inside
 * the document that carried it.
 */
export const graphcacheKeys = new Proxy<CacheKeyMap>(
  {
    CoinValue: () => null, // Don't normalize CoinValue - keep it embedded
    // The guest share read is a value object — its identity is the code that fetched it, which
    // is not a field of the type. Keep it embedded in its query document.
    LessonSharedByCode: () => null,
    /*
     * A question's options are numbered per question (`opt_1`…`opt_6`), so normalising them by id
     * merges every question's first option into one entity and the last write wins — a snapshot
     * holding two questions would render the second one's labels on both, and a tap would send an
     * id the student never saw the label for. They are per-question value objects with no identity
     * outside the question that asked them: keep them embedded.
     */
    CoachQuestionOption: () => null,
    CoachBoundQuestionOption: () => null,
    CoachStarterQuestionOption: () => null,
    /*
     * A recommendation card is a snapshot of what the student saw at that moment: its name, saved
     * state and progress as they were when the coach showed it. Normalising cards by id merges every
     * session's copy of the same record into one entity and the last write wins, so reading a newer
     * session would rename and re-badge the cards of every older one. Keep them embedded.
     */
    CoachJobRecommendation: () => null,
    CoachInstitutionRecommendation: () => null,
    CoachLearningAssetRecommendation: () => null,
    CoachLearningAssetCollectionRecommendation: () => null,
    CoachActionRecommendation: () => null,
    CoachIndustryRecommendation: () => null,
    CoachSurveyRecommendation: () => null,
    // One row per section, so flipping a section's public flag updates the settings list in place.
    FaqSectionSetting: (data: { section?: string }) => data.section ?? null,
    /*
     * A session's reviewer entry is keyed by the admin's id and carries that session's note count,
     * so one admin reviewing several sessions would collapse into one entry with the last count
     * written. Keep it embedded in its session.
     */
    AdminCoachSessionReviewer: () => null,
    /*
     * @namespaced container types have no id. Without a constant key they are stored as
     * keyless embedded objects, and each query writing through the namespace REPLACES the
     * embedded object — clobbering sibling fields other queries on the page rely on
     * (e.g. usersTrustSummary landing wiped findUser's cached link, blanking the trust
     * profile). A constant key makes each namespace a singleton entity whose fields merge.
     */
    CoreQueries: () => 'CoreQueries',
    UserQueries: () => 'UserQueries',
    SiteQueries: () => 'SiteQueries',
    OrgQueries: () => 'OrgQueries',
    SchoolQueries: () => 'SchoolQueries',
    SearchQueries: () => 'SearchQueries',
    CoreMutations: () => 'CoreMutations',
    AuthMutations: () => 'AuthMutations',
    UserMutations: () => 'UserMutations',
    OrgMutations: () => 'OrgMutations',
    FileMutations: () => 'FileMutations',
    UserFeedbackMutations: () => 'UserFeedbackMutations',
    LeadEmailMutations: () => 'LeadEmailMutations',
    SchoolMutations: () => 'SchoolMutations',
  },
  {
    get: (target, prop: string | symbol) => {
      if (typeof prop === 'string' && prop in target) {
        return target[prop];
      }
      // @ts-expect-error: Proxy fallback for unknown cache key structure
      return data => data?.id || null;
    },
  },
);
